import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of, switchMap } from 'rxjs';
import { I18nService } from '../core/i18n.service';
import { MatchScoreReportsApiService } from '../core/match-score-reports-api.service';
import {
  MatchScoreReportPayload,
  ScoreReportGameInput,
  ScoreReportScreenshotInput
} from '../core/match-score-reports-api.types';
import { TeamsApiService } from '../core/teams-api.service';
import { SessionService } from '../core/session.service';
import { BracketMatch, TeamSummary } from '../core/tournaments-api.types';
import { TournamentsApiService } from '../core/tournaments-api.service';

@Component({
  selector: 'app-match-score-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <p><a [routerLink]="backRoute()" [queryParams]="backQueryParams()">{{ backLabel() }}</a></p>
      <h1>{{ t('score.title') }}</h1>
      <p class="hint">{{ t('score.hint', { slug: tournamentSlug(), id: matchId() }) }}</p>
      <p class="hint">{{ t('score.hint2') }}</p>

      @if (loading()) {
        <p class="state">{{ t('score.loading') }}</p>
      } @else {
        @if (errorMessage()) {
          <p class="state state--error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="state state--success">{{ successMessage() }}</p>
        }

        @if (report(); as currentReport) {
          <div class="card">
            <p><strong>{{ t('score.status') }}</strong> {{ statusLabel(currentReport.status) }}</p>
            <p><strong>{{ t('score.editable') }}</strong> {{ showEditForm() ? t('score.yes') : t('score.no') }}</p>
            <p><strong>{{ t('score.reportingTeam') }}</strong> {{ reportingTeamName(currentReport.reporting_team_id) }}</p>
            <p><strong>{{ t('score.global') }}</strong> {{ currentReport.score_team1 }} - {{ currentReport.score_team2 }}</p>
            <p><strong>{{ t('score.updated') }}</strong> {{ currentReport.reported_at | date: 'short' }}</p>
          </div>
        } @else {
          <p class="state">{{ t('score.none') }}</p>
        }

        @if (report()?.contestation?.contested) {
          <div class="card card--contest">
            <p><strong>{{ t('score.contest.banner') }}</strong></p>
            <p>{{ t('score.contest.teamBanner', { team: report()!.contestation.contesting_team_name || report()!.contestation.contesting_team_id || '-' }) }}</p>
            @if (report()!.contestation.contestation_note) {
              <p>{{ t('score.contest.noteBanner', { value: report()!.contestation.contestation_note }) }}</p>
            }
          </div>
        }

        @if (showEditForm()) {
          <form (ngSubmit)="submit()" #form="ngForm" class="form">
            <h2>{{ report() ? t('score.form.edit') : t('score.form.new') }}</h2>
            <label>{{ t('score.form.team') }}
              <select name="reportingTeamId" required [(ngModel)]="selectedReportingTeamId" [disabled]="report() !== null">
                <option [ngValue]="null">{{ t('score.form.selectTeam') }}</option>
                @for (team of reportableTeams(); track team.id) {
                  <option [ngValue]="team.id">{{ team.name }}</option>
                }
              </select>
            </label>
            @if (reportableTeams().length === 0) {
              <p class="state state--error">{{ t('score.form.noTeam') }}</p>
            }

            <label>{{ teamScoreLabel(0) }}
              <input type="number" min="0" required name="scoreTeam1" [(ngModel)]="scoreTeam1" />
            </label>
            <label>{{ teamScoreLabel(1) }}
              <input type="number" min="0" required name="scoreTeam2" [(ngModel)]="scoreTeam2" />
            </label>
            <label>{{ t('score.form.note') }}
              <textarea name="note" rows="2" [(ngModel)]="note"></textarea>
            </label>

            <div class="block">
              <h3>{{ t('score.games.title') }}</h3>
              @for (game of games(); track $index; let i = $index) {
                <div class="row">
                  <input type="number" min="1" [placeholder]="t('score.games.num')" [ngModel]="game.game_number" (ngModelChange)="updateGame(i, 'game_number', $event)" name="gameNumber{{ i }}" />
                  <input type="text" [placeholder]="t('score.games.map')" [ngModel]="game.map_name || ''" (ngModelChange)="updateGame(i, 'map_name', $event)" name="mapName{{ i }}" />
                  <input type="number" min="0" [placeholder]="t('score.games.t1')" [ngModel]="game.team1_score" (ngModelChange)="updateGame(i, 'team1_score', $event)" name="team1Score{{ i }}" />
                  <input type="number" min="0" [placeholder]="t('score.games.t2')" [ngModel]="game.team2_score" (ngModelChange)="updateGame(i, 'team2_score', $event)" name="team2Score{{ i }}" />
                  <button type="button" class="secondary" (click)="removeGame(i)">{{ t('score.remove') }}</button>
                </div>
              }
              <button type="button" class="secondary" (click)="addGame()">{{ t('score.games.add') }}</button>
            </div>

            <div class="block">
              <h3>{{ t('score.shots.title') }}</h3>
              <p class="hint">{{ t('score.shots.help') }}</p>
              <label>{{ t('score.shots.file') }}
                <input type="file" accept="image/png,image/jpeg,image/webp" (change)="onScreenshotSelected($event)" />
              </label>

              @if (screenshotError()) {
                <p class="state state--error">{{ screenshotError() }}</p>
              }

              @if (screenshotPreviewUrl(); as previewUrl) {
                <div class="proof-preview">
                  <img [src]="previewUrl" [alt]="t('score.shots.previewAlt')" />
                  <div class="proof-preview-copy">
                    <p class="proof-title">{{ screenshotStateLabel() }}</p>
                    @if (screenshotFileName()) {
                      <p class="proof-meta">{{ screenshotFileName() }}</p>
                    }
                  </div>
                  <button type="button" class="secondary" (click)="clearScreenshot()">{{ t('score.remove') }}</button>
                </div>
              } @else {
                <p class="state">{{ t('score.shots.none') }}</p>
              }
            </div>

            <button type="submit" [disabled]="submitting() || !form.valid || !selectedReportingTeamId || reportableTeams().length === 0">
              {{ submitting() ? t('score.submitting') : t('score.submit') }}
            </button>
          </form>
        } @else if (showContestForm()) {
          <div class="form">
            <h2>{{ hasCurrentTeamContested() ? t('score.contest.edit') : t('score.contest.title') }}</h2>
            <p class="hint">{{ t('score.contest.help') }}</p>

            <label>{{ t('score.contest.team') }}
              <select name="contestingTeamId" [(ngModel)]="selectedContestingTeamId">
                <option [ngValue]="null">{{ t('score.form.selectTeam') }}</option>
                @for (team of contestingTeams(); track team.id) {
                  <option [ngValue]="team.id">{{ team.name }}</option>
                }
              </select>
            </label>

            <label>{{ t('score.contest.note') }}
              <textarea name="contestationNote" rows="3" [(ngModel)]="contestationNote"></textarea>
            </label>

            <button
              type="button"
              [disabled]="contesting() || !selectedContestingTeamId || !contestationNote.trim()"
              (click)="submitContest()"
            >
              {{ contesting() ? t('score.contest.submitting') : contestActionLabel() }}
            </button>
          </div>
        } @else {
          <p class="state">{{ t('score.locked') }}</p>
        }
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 860px; }
    h1 { margin: 0.2rem 0 0.5rem; font-size: 1.4rem; color: var(--text-strong); }
    h2 { margin: 0 0 0.6rem; font-size: 1.1rem; color: var(--text-strong); }
    h3 { margin: 0 0 0.4rem; font-size: 0.98rem; color: var(--text-strong); }
    .hint { margin: 0.2rem 0; color: var(--text-muted); }
    .card {
      border: 1px solid var(--line-soft);
      border-radius: 12px;
      background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%);
      padding: 0.9rem;
      margin: 0.8rem 0;
      box-shadow: var(--shadow-soft);
    }
    .card p { margin: 0.2rem 0; color: var(--text-muted); }
    .card strong { color: var(--text-strong); }
    .card--contest {
      border-color: rgba(236, 165, 69, 0.42);
      background: linear-gradient(180deg, rgba(46, 31, 12, 0.92) 0%, rgba(34, 23, 10, 0.94) 100%);
    }
    .form {
      border: 1px solid var(--line-soft);
      border-radius: 12px;
      background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%);
      padding: 0.9rem;
      display: grid;
      gap: 0.7rem;
      box-shadow: var(--shadow-soft);
    }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: var(--text-muted); }
    input, textarea, select {
      padding: 0.55rem 0.6rem;
      border: 1px solid rgba(124, 157, 211, 0.32);
      border-radius: 8px;
      background: rgba(10, 18, 31, 0.78);
      color: var(--text-strong);
    }
    input::placeholder, textarea::placeholder { color: var(--text-subtle); }
    .block {
      border: 1px solid rgba(124, 157, 211, 0.2);
      border-radius: 10px;
      padding: 0.7rem;
      display: grid;
      gap: 0.5rem;
      background: rgba(10, 18, 31, 0.34);
    }
    .row { display: grid; grid-template-columns: 90px 1fr 90px 90px auto; gap: 0.45rem; align-items: center; }
    .row input[type='url'] { min-width: 170px; }
    .proof-preview {
      display: grid;
      grid-template-columns: 140px 1fr auto;
      gap: 0.65rem;
      align-items: center;
      border: 1px solid rgba(124, 157, 211, 0.22);
      border-radius: 10px;
      padding: 0.65rem;
      background: rgba(10, 18, 31, 0.34);
    }
    .proof-preview img {
      width: 140px;
      height: 90px;
      object-fit: cover;
      border-radius: 8px;
      border: 1px solid rgba(124, 157, 211, 0.26);
      background: rgba(6, 11, 20, 0.95);
    }
    .proof-preview-copy { min-width: 0; }
    .proof-title { margin: 0; color: var(--text-strong); font-weight: 700; }
    .proof-meta { margin: 0.25rem 0 0; color: var(--text-muted); overflow-wrap: anywhere; }
    button {
      border: 1px solid rgba(125, 176, 255, 0.35);
      border-radius: 8px;
      padding: 0.55rem 0.8rem;
      background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%);
      color: #091325;
      font-weight: 700;
    }
    .secondary {
      background: rgba(101, 145, 216, 0.18);
      color: #d7e7ff;
      border-color: rgba(112, 164, 255, 0.42);
    }
    .state { margin: 0.55rem 0; color: var(--text-muted); }
    .state--success { color: #85e2a3; }
    .state--error { color: #ff8f8f; }
    a { color: var(--accent); text-decoration: none; }
    @media (max-width: 820px) {
      .row { grid-template-columns: 1fr; }
      .proof-preview { grid-template-columns: 1fr; }
      .proof-preview img { width: 100%; height: auto; max-height: 240px; }
    }
  `
})
export class MatchScoreReportPageComponent {
  private static readonly ALLOWED_SCREENSHOT_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
  private static readonly MAX_SCREENSHOT_SIZE_BYTES = 5 * 1024 * 1024;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(MatchScoreReportsApiService);
  private readonly teamsApi = inject(TeamsApiService);
  private readonly tournamentsApi = inject(TournamentsApiService);
  private readonly i18n = inject(I18nService);
  private readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tournamentSlug = signal('');
  protected readonly matchId = signal(0);
  protected readonly resolvedMatchId = signal<number | null>(null);
  protected readonly fromMyMatches = signal(false);
  protected readonly myTeamIds = signal<ReadonlySet<number>>(new Set());
  protected readonly report = signal<MatchScoreReportPayload | null>(null);
  protected readonly reportableTeams = signal<TeamSummary[]>([]);
  protected readonly matchTeams = signal<(TeamSummary | null)[]>([null, null]);
  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly contesting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly screenshotPreviewUrl = signal<string | null>(null);
  protected readonly screenshotFileName = signal<string | null>(null);
  protected readonly screenshotError = signal<string | null>(null);

  protected selectedReportingTeamId: number | null = null;
  protected selectedContestingTeamId: number | null = null;
  protected scoreTeam1 = 0;
  protected scoreTeam2 = 0;
  protected note = '';
  protected contestationNote = '';

  protected readonly games = signal<ScoreReportGameInput[]>([]);
  protected readonly screenshots = signal<ScoreReportScreenshotInput[]>([]);
  private selectedScreenshotFile: File | null = null;
  private generatedScreenshotPreviewUrl: string | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.revokeGeneratedScreenshotPreview());

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          this.fromMyMatches.set(params.get('from') === 'my-matches');
        }
      });

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          const slug = params.get('slug') ?? '';
          const matchId = Number(params.get('matchId') ?? '0');
          this.tournamentSlug.set(slug);
          this.matchId.set(matchId);
          this.loadPage();
        }
      });
  }

  protected submit(): void {
    if (!this.selectedReportingTeamId) {
      this.errorMessage.set(this.t('score.selectTeamError'));
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.api
      .upsertReport(this.tournamentSlug(), this.resolvedMatchId() ?? this.matchId(), {
        reporting_team_id: Number(this.selectedReportingTeamId),
        score_team1: Number(this.scoreTeam1),
        score_team2: Number(this.scoreTeam2),
        note: this.note.trim(),
        games: this.games().filter((game) => game.game_number > 0),
        screenshots: this.preservedLegacyScreenshots(),
        screenshot_file: this.selectedScreenshotFile,
        keep_existing_screenshot: this.shouldKeepExistingScreenshot(),
        remove_existing_screenshot: this.shouldRemoveExistingScreenshot()
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ score_report }) => {
          this.report.set(score_report);
          this.prefillForm(score_report);
          this.successMessage.set(this.t('score.saved'));
          this.submitting.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.errorMessage.set(this.extractError(error) ?? this.t('score.saveError'));
          this.submitting.set(false);
        }
      });
  }

  protected submitContest(): void {
    if (!this.selectedContestingTeamId || !this.contestationNote.trim()) {
      this.errorMessage.set(this.t('score.contest.error'));
      return;
    }

    this.contesting.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.api
      .contestReport(this.tournamentSlug(), this.resolvedMatchId() ?? this.matchId(), {
        contesting_team_id: Number(this.selectedContestingTeamId),
        contestation_note: this.contestationNote.trim()
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ score_report }) => {
          this.report.set(score_report);
          this.prefillForm(score_report);
          this.successMessage.set(this.t('score.contest.saved'));
          this.contesting.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.errorMessage.set(this.extractError(error) ?? this.t('score.contest.saveError'));
          this.contesting.set(false);
        }
      });
  }

  protected addGame(): void {
    this.games.update((current) => [...current, { game_number: current.length + 1, map_name: '', team1_score: 0, team2_score: 0 }]);
  }

  protected removeGame(index: number): void {
    this.games.update((current) => current.filter((_, i) => i != index));
  }

  protected updateGame(index: number, field: keyof ScoreReportGameInput, value: string | number): void {
    this.games.update((current) => {
      const clone = [...current];
      const row = { ...clone[index] };
      (row[field] as string | number | undefined) =
        typeof value == 'string' && (field == 'game_number' || field == 'team1_score' || field == 'team2_score')
          ? Number(value)
          : value;
      clone[index] = row;
      return clone;
    });
  }

  protected onScreenshotSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;

    if (!MatchScoreReportPageComponent.ALLOWED_SCREENSHOT_TYPES.has(file.type)) {
      this.screenshotError.set(this.t('score.shots.invalidType'));
      input!.value = '';
      return;
    }

    if (file.size > MatchScoreReportPageComponent.MAX_SCREENSHOT_SIZE_BYTES) {
      this.screenshotError.set(this.t('score.shots.tooLarge'));
      input!.value = '';
      return;
    }

    this.screenshotError.set(null);
    this.selectedScreenshotFile = file;
    this.screenshotFileName.set(file.name);
    this.setGeneratedScreenshotPreview(URL.createObjectURL(file));
    this.screenshots.set([]);
    input!.value = '';
  }

  protected clearScreenshot(): void {
    const hadExistingScreenshot = this.screenshots().length > 0;

    this.selectedScreenshotFile = null;
    this.revokeGeneratedScreenshotPreview();
    this.screenshotPreviewUrl.set(null);
    this.screenshotFileName.set(null);
    this.screenshotError.set(null);

    if (hadExistingScreenshot) {
      this.screenshots.set([]);
    }
  }

  protected isEditableStatus(status: string): boolean {
    return status == 'reported' || status == 'in_review' || status == 'rejected';
  }

  protected showEditForm(): boolean {
    const report = this.report();
    if (!report) return true;
    if (!this.isEditableStatus(report.status)) return false;
    return this.myTeamIds().has(report.reporting_team_id);
  }

  protected showContestForm(): boolean {
    const report = this.report();
    if (!report) return false;
    if (this.showEditForm()) return false;
    return this.isContestableStatus(report.status) && this.contestingTeams().length > 0;
  }

  protected contestingTeams(): TeamSummary[] {
    const report = this.report();
    if (!report) return [];
    return this.reportableTeams().filter((team) => team.id !== report.reporting_team_id);
  }

  protected hasCurrentTeamContested(): boolean {
    const contestation = this.report()?.contestation;
    if (!contestation?.contested || !contestation.contesting_team_id) return false;
    return this.myTeamIds().has(contestation.contesting_team_id);
  }

  protected contestActionLabel(): string {
    return this.hasCurrentTeamContested() ? this.t('score.contest.update') : this.t('score.contest.submit');
  }

  protected statusLabel(status: string): string {
    if (status === 'reported') {
      return this.t('score.status.reported');
    }
    if (status === 'in_review') {
      return this.t('score.status.review');
    }
    if (status === 'validated') {
      return this.t('score.status.validated');
    }
    if (status === 'rejected') {
      return this.t('score.status.rejected');
    }
    return status;
  }

  protected backRoute(): string[] {
    if (this.fromMyMatches()) {
      return ['/player/matches'];
    }

    return ['/tournaments', this.tournamentSlug(), 'bracket'];
  }

  protected backQueryParams(): Record<string, string> | null {
    if (this.fromMyMatches()) {
      return null;
    }

    return null;
  }

  protected backLabel(): string {
    return this.fromMyMatches() ? this.t('score.backToMatches') : this.t('score.back');
  }

  protected teamScoreLabel(index: number): string {
    const team = this.matchTeams()[index];
    const fallback = index === 0 ? this.t('score.form.team1') : this.t('score.form.team2');
    return team ? this.t('score.form.teamNamed', { team: team.name }) : fallback;
  }

  protected reportingTeamName(teamId: number): string {
    return this.matchTeams().find((team) => team?.id === teamId)?.name ?? String(teamId);
  }

  protected screenshotStateLabel(): string {
    if (this.selectedScreenshotFile) {
      return this.t('score.shots.selected');
    }

    return this.t('score.shots.current');
  }

  private loadPage(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    forkJoin({
      bracket: this.tournamentsApi.getTournamentBracket(this.tournamentSlug()),
      myTeams: this.teamsApi.getMyTeams()
    })
      .pipe(
        switchMap(({ bracket, myTeams }) => {
          const allMatches = bracket.bracket.rounds.flatMap((round) => round.matches);
          const match =
            allMatches.find((item) => item.id == this.matchId()) ??
            allMatches.find((item) => item.match_number == this.matchId());

          if (!match) {
            throw new Error('Match not found');
          }

          this.matchTeams.set(this.resolveMatchTeams(match));
          this.resolvedMatchId.set(match.id);
          const matchTeams = (match.slots ?? []).map((slot) => slot.team).filter((team): team is TeamSummary => !!team);
          const myTeamIds = new Set(myTeams.teams.map((team) => team.id));
          this.myTeamIds.set(myTeamIds);
          this.reportableTeams.set(matchTeams.filter((team) => myTeamIds.has(team.id)));

          return forkJoin({
            scoreReport: this.api.getReport(this.tournamentSlug(), match.id),
            ready: of(true)
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ scoreReport }) => {
          this.report.set(scoreReport.score_report);
          this.prefillForm(scoreReport.score_report);
          this.loading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.errorMessage.set(this.extractError(error) ?? this.t('score.loadError'));
          this.loading.set(false);
        }
      });
  }

  private prefillForm(report: MatchScoreReportPayload | null): void {
    if (!report) {
      this.selectedReportingTeamId = this.reportableTeams()[0]?.id ?? null;
      this.selectedContestingTeamId = null;
      this.scoreTeam1 = 0;
      this.scoreTeam2 = 0;
      this.note = '';
      this.contestationNote = '';
      this.games.set([]);
      this.screenshots.set([]);
      this.selectedScreenshotFile = null;
      this.revokeGeneratedScreenshotPreview();
      this.screenshotPreviewUrl.set(null);
      this.screenshotFileName.set(null);
      this.screenshotError.set(null);
      return;
    }

    this.selectedReportingTeamId = report.reporting_team_id;
    this.selectedContestingTeamId = this.defaultContestingTeamId(report);
    this.scoreTeam1 = report.score_team1;
    this.scoreTeam2 = report.score_team2;
    this.note = report.note ?? '';
    this.contestationNote = this.hasContestationByCurrentTeam(report) ? report.contestation.contestation_note ?? '' : '';
    this.games.set(report.games ?? []);
    this.screenshots.set(report.screenshots ?? []);
    this.selectedScreenshotFile = null;
    this.revokeGeneratedScreenshotPreview();
    const currentProofUrl = report.screenshots?.[0]?.screenshot_url ?? null;
    this.screenshotPreviewUrl.set(currentProofUrl);
    this.screenshotFileName.set(currentProofUrl ? this.filenameFromUrl(currentProofUrl) : null);
    this.screenshotError.set(null);
  }

  private extractError(error: unknown): string | null {
    const err = error as { error?: { error?: string; errors?: string[] } };
    if (err?.error?.error) {
      return this.humanizeApiError(err.error.error);
    }
    if (Array.isArray(err?.error?.errors) && err.error.errors.length > 0) {
      return err.error.errors.join(' | ');
    }
    return null;
  }

  private humanizeApiError(apiError: string): string {
    if (apiError === 'Only team members can report score for this match') {
      return this.t('score.form.noTeam');
    }
    if (apiError === 'This score report cannot be modified in its current status') {
      return this.t('score.locked');
    }
    if (apiError === 'Reporting team cannot contest its own report') {
      return this.t('score.contest.ownTeam');
    }
    if (apiError === 'Score report can no longer be contested') {
      return this.t('score.contest.locked');
    }
    return apiError;
  }

  private handleUnauthorized(): void {
    this.session.clearToken();
    void this.router.navigateByUrl('/player/login');
  }

  private resolveMatchTeams(match: BracketMatch): (TeamSummary | null)[] {
    return [0, 1].map((index) => match.slots?.[index]?.team ?? null);
  }

  private preservedLegacyScreenshots(): ScoreReportScreenshotInput[] {
    if (this.selectedScreenshotFile) {
      return [];
    }

    if (this.shouldKeepExistingScreenshot()) {
      return this.screenshots();
    }

    return [];
  }

  private shouldKeepExistingScreenshot(): boolean {
    return this.selectedScreenshotFile == null && this.screenshots().length > 0;
  }

  private shouldRemoveExistingScreenshot(): boolean {
    return (
      this.selectedScreenshotFile == null &&
      this.screenshots().length === 0 &&
      (this.report()?.screenshots?.length ?? 0) > 0
    );
  }

  private setGeneratedScreenshotPreview(url: string): void {
    this.revokeGeneratedScreenshotPreview();
    this.generatedScreenshotPreviewUrl = url;
    this.screenshotPreviewUrl.set(url);
  }

  private revokeGeneratedScreenshotPreview(): void {
    if (!this.generatedScreenshotPreviewUrl) return;

    URL.revokeObjectURL(this.generatedScreenshotPreviewUrl);
    this.generatedScreenshotPreviewUrl = null;
  }

  private filenameFromUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return decodeURIComponent(parsed.pathname.split('/').pop() || url);
    } catch {
      return url;
    }
  }

  private isContestableStatus(status: string): boolean {
    return status === 'reported' || status === 'in_review';
  }

  private hasContestationByCurrentTeam(report: MatchScoreReportPayload): boolean {
    const contestingTeamId = report.contestation.contesting_team_id;
    if (!report.contestation.contested || !contestingTeamId) return false;
    return this.myTeamIds().has(contestingTeamId);
  }

  private defaultContestingTeamId(report: MatchScoreReportPayload): number | null {
    if (this.hasContestationByCurrentTeam(report) && report.contestation.contesting_team_id) {
      return report.contestation.contesting_team_id;
    }

    return this.reportableTeams().find((team) => team.id !== report.reporting_team_id)?.id ?? null;
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
